// 28. 10. 2026 (owner, players' feedback): the order sheet keeps what the player set while they follow one of its links
// (Repair on a worn item, Lock, the pack's Open, Cook one) and come back. One draft per survivor and zone in this tab's
// sessionStorage, written on every change, read when the sheet opens while it is recent, removed when a run is sent.
// The storage can be missing or refuse (a private window, blocked site data): every access is guarded, and the sheet
// then simply starts from the last orders as before.

import type { AmbushStyle, MealId, Retreat, Stance } from '@lastday/shared';

export interface OrderDraft {
  /** When it was written (Date.now()). */
  at: number;
  duration: number;
  stance: Stance;
  ambush: AmbushStyle;
  retreat: Retreat;
  offer: string[];
  want: string[];
  bring: { painkillers: boolean; stim: boolean; hollowPoint: boolean };
  /** The counters the player set by hand (null: the sheet's suggestion). */
  molotov: number | null;
  bandage: number | null;
  medkit: number | null;
  feed: number | null;
  truck: boolean;
  revenge: string | null;
  meal: MealId | null;
}

/** A draft older than this is forgotten (the plan, energy and Storage have moved on). */
export const ORDER_DRAFT_TTL_MS = 30 * 60_000;

const PREFIX = 'ld_orders:';
const keyOf = (survivorId: string, zone: string) => `${PREFIX}${survivorId}:${zone}`;

export function readOrderDraft(survivorId: string, zone: string, now = Date.now()): OrderDraft | null {
  try {
    const raw = window.sessionStorage.getItem(keyOf(survivorId, zone));
    if (!raw) return null;
    const d = JSON.parse(raw) as Partial<OrderDraft> | null;
    if (!d || typeof d.at !== 'number' || now - d.at > ORDER_DRAFT_TTL_MS || d.at - now > 60_000) return null;
    if (typeof d.duration !== 'number' || typeof d.stance !== 'string' || !Array.isArray(d.offer) || !Array.isArray(d.want) || !d.bring) return null;
    return d as OrderDraft;
  } catch {
    return null;
  }
}

export function writeOrderDraft(survivorId: string, zone: string, draft: Omit<OrderDraft, 'at'>): void {
  try {
    window.sessionStorage.setItem(keyOf(survivorId, zone), JSON.stringify({ ...draft, at: Date.now() }));
  } catch {
    // No storage: the sheet works without a draft.
  }
}

/**
 * Every draft of this tab: a run sent any way (the sheet, Same orders, a raid) leaves Storage, energy and the last orders
 * changed, so no zone's draft is restored after it.
 */
export function clearOrderDrafts(): void {
  try {
    const s = window.sessionStorage;
    for (const k of Object.keys(s)) if (k.startsWith(PREFIX)) s.removeItem(k);
  } catch {
    // Nothing to clear.
  }
}
